import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  HomeIcon,
  DocumentPlusIcon,
  CalendarDaysIcon,
  VideoCameraIcon,
  SparklesIcon,
  ShieldCheckIcon,
  BellIcon,
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
  UserCircleIcon,
  Cog6ToothIcon
} from "@heroicons/react/24/outline";
import { HomeIcon as HomeIconSolid } from "@heroicons/react/24/solid";

import logoImg from "../../assets/images/langingpage/logo_image.png";
import Modal from "@lxp-app/components/atoms/modal";
import LogoutConfirmationModal from "@lxp-app/components/molecules/modal-children/logoutConfirmModal";
import { RootState } from "@assessment/store/store";
import UserRole from "@lxp-app/utils/roles";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

interface NavItem {
  id?: string;
  label: string;
  icon: React.ElementType;
  link?: string;
  activeIcon?: React.ElementType;
  active?: boolean;
  rolesAllowed?: string[];
  disabled?: boolean;
}

const getQuickAccessItems = (t: any): NavItem[] => [
  {
    label: t("assessment.sidebar.questionPaperAuthoring"),
    icon: DocumentPlusIcon,
    link: "/authoring/question-paper-authoring",
    rolesAllowed: [UserRole.ASSESSMENT_CREATOR]
  },
  {
    label: t("assessment.sidebar.assessmentLibrary"),
    icon: CalendarDaysIcon,
    link: "/authoring/assessment-authoring",
    rolesAllowed: [UserRole.ASSESSMENT_ADMIN, UserRole.RECRUITER]
  },
  {
    label: t("assessment.sidebar.scheduleInterview"),
    icon: VideoCameraIcon,
    link: "/interview",
    rolesAllowed: [UserRole.INTERVIEW_ADMIN]
  },
  {
    label: t("assessment.sidebar.generateQuestions"),
    icon: SparklesIcon,
    link: "/generate-questions",
    rolesAllowed: [UserRole.AI_AUTHORING_CREATOR]
  },
  {
    label: t("assessment.sidebar.myPreferences"),
    icon: Cog6ToothIcon,
    link: "/my-preferences",
    rolesAllowed: ["*"]
  }
];

const getMyAccountItems = (t: any): NavItem[] => [
  // { label: "Content Admin", icon: ShieldCheckIcon, link: "/authoring" },
  // { label: "Notification", icon: BellIcon, link: "/notifications" },
  {
    id: "logout",
    label: t("assessment.sidebar.logout"),
    icon: ArrowRightStartOnRectangleIcon
  }
];

export function Sidebar() {
  const location = useLocation();
  const { t } = useTranslation();
  const homeLabel = t("assessment.sidebar.home");
  const quickAccessLabel = t("assessment.sidebar.quickAccess");
  const roleManagementLabel = t("assessment.sidebar.roleManagement");
  const noAccessToLinkLabel = t("assessment.sidebar.noAccessToLink");
  const expandSidebarLabel = t("assessment.sidebar.expandSidebar");
  const collapseSidebarLabel = t("assessment.sidebar.collapseSidebar");
  const expandLabel = t("assessment.sidebar.expand");
  const collapseLabel = t("assessment.sidebar.collapse");

  const [isOpen, setIsOpen] = useState(false);
  const quickAccessItems = getQuickAccessItems(t);
  const myAccountItems = getMyAccountItems(t);

  const isActivePath = (link?: string) => {
    if (!link) return false;
    if (link === "/") return location.pathname === "/";
    return (
      location.pathname === link || location.pathname.startsWith(`${link}/`)
    );
  };

  const [isCollapsed, setIsCollapsed] = useState(
    () => location.pathname !== "/"
  );
  const [manualOverride, setManualOverride] = useState(false);
  const [logoutPopUpOpen, setLogoutPopUpOpen] = useState<boolean>(false);

  const handleLogoutPopUp = async () => {
    setLogoutPopUpOpen(!logoutPopUpOpen);
  };

  const User = useSelector((state: RootState) => state.UserInfoSlice.profile);

  let allowedQuickAccessItems = [];
  allowedQuickAccessItems = useMemo(() => {
    const items = [...quickAccessItems];

    if (
      User?.role?.includes(UserRole.ROLE_ADMIN) &&
      !items.some((item) => item.label === roleManagementLabel)
    ) {
      items.push({
        label: roleManagementLabel,
        icon: UserCircleIcon,
        link: "/super-admin",
        rolesAllowed: [UserRole.ROLE_ADMIN]
      });
    }

    return items.map((item) => {
      if (item.rolesAllowed) {
        if (item.rolesAllowed.includes("*")) {
          return { ...item, disabled: false };
        }
        const isAllowed = item.rolesAllowed.some((role: any) =>
          User?.role?.includes(role)
        );
        return { ...item, disabled: !isAllowed };
      }
      return { ...item, disabled: true };
    });
  }, [User, quickAccessItems, roleManagementLabel]);

  useEffect(() => {
    if (manualOverride) return;
    if (location.pathname === "/") {
      setIsCollapsed(false);
    } else {
      setIsCollapsed(true);
    }
  }, [location.pathname, manualOverride]);

  return (
    <>
      {/* Mobile toggle button */}
      {!isOpen && (
        <button
          className="md:hidden fixed top-2 right-4 z-40 p-2 text-gray-600   hover:bg-gray-100   rounded-lg transition-colors"
          onClick={() => setIsOpen(true)}
        >
          <Bars3Icon className="size-8" />
        </button>
      )}
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} ${isCollapsed ? "md:w-[84px] md:min-w-[84px] w-[260px] min-w-[260px]" : "w-[260px] min-w-[260px]"} bg-[#f5f5f5] h-screen flex flex-col overflow-y-auto`}
      >
        {/* Close Button Mobile Only */}
        <button
          className="md:hidden absolute top-6 right-4 p-2 text-gray-500 hover:text-gray-700  "
          onClick={() => setIsOpen(false)}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div
          className={`pt-6 pb-4 ${isCollapsed ? "px-3" : "px-6"} flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}
        >
          <Link to={"/"}>
            <img
              src={logoImg}
              alt="GlobalLogic Skill Quotient"
              className={`w-auto ${isCollapsed ? "hidden" : "h-12 block"}`}
            />
          </Link>
          <button
            type="button"
            className="hidden md:flex items-center justify-center p-1 rounded-md text-[#484f6b] hover:bg-[#e9ebf2] transition-colors"
            onClick={() => {
              setManualOverride(true);
              setIsCollapsed((prev) => !prev);
            }}
            aria-label={isCollapsed ? expandSidebarLabel : collapseSidebarLabel}
          >
            {isCollapsed ? (
              <Tooltip title={expandLabel} placement="right">
                <ChevronDoubleRightIcon className="size-6" />
              </Tooltip>
            ) : (
              <Tooltip title={collapseLabel} placement="right">
                <ChevronDoubleLeftIcon className="size-6" />
              </Tooltip>
            )}
          </button>
        </div>

        {/* Main Nav */}
        <nav
          className={`pt-4 flex flex-col gap-5 ${isCollapsed ? "px-3" : "px-6"}`}
        >
          <Tooltip title={isCollapsed ? homeLabel : ""} placement="right">
            <Link
              to="/"
              className={`flex items-center text-base ${isCollapsed ? "justify-center" : "gap-3"} ${
                location.pathname === "/" ? "text-[#343434]" : "text-[#484f6b]"
              }`}
            >
              {location.pathname === "/" ? (
                <HomeIconSolid className="w-6 h-6 text-[#343434]" />
              ) : (
                <HomeIcon className="w-6 h-6" />
              )}
              {!isCollapsed && homeLabel}
            </Link>
          </Tooltip>
        </nav>

        {/* Quick Access */}
        <div className={`mt-8 ${isCollapsed ? "px-3" : "px-6"}`}>
          {!isCollapsed && (
            <div className="flex items-center gap-3 mb-5">
              <span
                className="text-[#858a9b] text-[13px] uppercase tracking-wider whitespace-nowrap"
                style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
              >
                {quickAccessLabel}
              </span>
              <div className="h-px bg-[#d9d9d9] flex-1" />
            </div>
          )}
          <nav className="flex flex-col gap-5">
            {allowedQuickAccessItems.map((item) => {
              if (item.disabled) {
                return (
                  <Tooltip
                    title={
                      isCollapsed
                        ? t("assessment.sidebar.noAccessWithLabel", {
                            label: item.label
                          })
                        : noAccessToLinkLabel
                    }
                    placement={isCollapsed ? "right" : "bottom"}
                    key={item.label}
                  >
                    <div
                      className={`flex items-center text-base cursor-not-allowed opacity-50 text-content ${isCollapsed ? "justify-center" : "gap-3"}`}
                    >
                      <item.icon className="w-6 h-6" />
                      {!isCollapsed && item.label}
                    </div>
                  </Tooltip>
                );
              }

              return (
                <Tooltip
                  title={isCollapsed ? item.label : ""}
                  placement="right"
                  key={item.label}
                >
                  <Link
                    to={item.link || "/"}
                    className={`flex items-center text-base hover:text-primary ${isCollapsed ? "justify-center" : "gap-3"} ${
                      isActivePath(item.link)
                        ? "text-[#343434]"
                        : "text-content"
                    } cursor-pointer`}
                  >
                    <item.icon className="w-6 h-6" />
                    {!isCollapsed && item.label}
                  </Link>
                </Tooltip>
              );
            })}
          </nav>
        </div>

        {/* My Account */}
        <div className={`mt-6 pb-8 ${isCollapsed ? "px-3" : "px-6"}`}>
          {/* <div className="flex items-center gap-3 mb-5">
            <span
              className="text-[#858a9b] text-[13px] uppercase tracking-wider whitespace-nowrap"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
            >
              My Account
            </span>
            <div className="h-px bg-[#d9d9d9] flex-1" />
          </div> */}
          <nav className="flex flex-col gap-5">
            {myAccountItems.map((item) => {
              if (item.id === "logout") {
                return (
                  <Tooltip
                    title={isCollapsed ? item.label : ""}
                    placement="right"
                    key={item.label}
                  >
                    <button
                      onClick={handleLogoutPopUp}
                      className={`flex items-center text-base hover:text-primary text-content cursor-pointer ${isCollapsed ? "justify-center" : "gap-3"}`}
                    >
                      <item.icon className="w-6 h-6" />
                      {!isCollapsed && item.label}
                    </button>
                  </Tooltip>
                );
              }
              return (
                <Link
                  to={item.link || "/"}
                  key={item.label}
                  className={`flex items-center text-base hover:text-primary ${isCollapsed ? "justify-center" : "gap-3"} ${
                    isActivePath(item.link) ? "text-[#343434]" : "text-content"
                  }`}
                >
                  <item.icon className="w-6 h-6" />
                  {!isCollapsed && item.label}
                </Link>
              );
            })}
          </nav>
        </div>
        {User?.name && User?.email && (
          <div
            className={`mt-auto absolute bottom-12 border-t border-[#d9d9d9] w-full ${isCollapsed ? "p-3" : "p-4"}`}
          >
            <div
              className={`flex ${isCollapsed ? "justify-center" : ""} items-center gap-2`}
            >
              <div className="size-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                <span
                  className="text-white text-[14px]"
                  style={{ fontWeight: 600 }}
                >
                  {User?.name?.firstName?.charAt(0)}
                  {User?.name?.lastName?.charAt(0)}{" "}
                </span>
              </div>
              {!isCollapsed && (
                <div className="min-w-0">
                  <div
                    className="text-secondary text-base truncate"
                    style={{ fontWeight: 600 }}
                  >
                    {User?.name?.fullName}
                  </div>
                  <div className="text-[#858a9b] text-xs truncate">
                    {User?.email}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
      <Modal
        dialogueClassName="sm:max-w-3xl"
        open={logoutPopUpOpen}
        setOpen={setLogoutPopUpOpen}
        children={
          <LogoutConfirmationModal
            setOpen={handleLogoutPopUp}
            onConfirm={() => {}}
          />
        }
      />
    </>
  );
}
